import { useEffect, useRef } from 'react';
import { useRouter, useRootNavigationState } from 'expo-router';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSnackbar } from './useSnackbar';
import {
  getPendingDeepLink,
  clearPendingDeepLink,
  refetchDeepLinkQueries,
  getApiErrorMessage,
} from '../helpers/deepLink.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import type { DeepLinkProcessResponse } from '../types/deepLink.types';

/** Navigation stability delay in ms. Prevents "navigate before mount" on native. */
const NAV_STABILITY_DELAY_MS = 300;

/**
 * Hydration-aware hook that processes pending deep links after sign-in.
 * Call this in the authenticated tab layout (app/app/_layout.tsx).
 *
 * Gates on:
 *   1. Auth loaded + signed in
 *   2. Navigation tree hydrated (rootNavState.key exists)
 *   3. Only runs once per mount cycle (processedRef)
 */
export function useDeepLinkHandler() {
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const rootNavState = useRootNavigationState();
  const apiClient = useApiClient();
  const router = useRouter();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const processedRef = useRef(false);
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  useEffect(() => {
    // Gate 1: Auth must be loaded and signed in
    if (!isLoaded || !isSignedIn) return;
    // Gate 2: Navigation tree must be hydrated
    if (!rootNavState?.key) return;
    // Gate 3: Only process once per mount cycle
    if (processedRef.current) return;
    processedRef.current = true;

    const timer = setTimeout(async () => {
      const pending = await getPendingDeepLink();
      if (!pending) return;

      try {
        const response = await apiClientRef.current<DeepLinkProcessResponse>(
          API_ENDPOINTS.deepLinks.process,
          { method: 'POST', data: { token: pending.token } }
        );

        if (response.data?.success && response.data?.data) {
          await clearPendingDeepLink();
          await refetchDeepLinkQueries(queryClient);
          const { redirectTo, successMessage } = response.data.data;
          router.replace((redirectTo || '/app/') as '/');
          if (successMessage) {
            showSnackbar(successMessage, 'success');
          }
        } else {
          const errorMsg = response.data?.error || 'Invite link could not be processed';
          await clearPendingDeepLink();
          showSnackbar(errorMsg, 'error');
        }
      } catch (err) {
        const apiMessage = getApiErrorMessage(err, '');

        if (apiMessage) {
          // Deterministic failure (expired, used, not found) -- clear the link
          await clearPendingDeepLink();
          showSnackbar(apiMessage, 'error');
        }
        // Network/transient failure: keep in storage for retry on next app open
      }
    }, NAV_STABILITY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isLoaded, isSignedIn, rootNavState?.key, router, showSnackbar, queryClient]);
}
